import dynamic from 'next/dynamic';
import React, { ComponentType } from 'react';
import { Modules } from '~/lib/data-contract';
import { ErrorBoundary } from '~/shared/utils/errorBoundary/ErrorBoundary';
import { ErrorFallbackContent } from '../ErrorFallbackContent';
import M130ProductDetail from '../product-details/m130product-detail.component';
import M131ProductDetail from '../product-details/m131product-detail.component';
import M140ProductsList from '../M140ProductsList';

type ModuleType = NonNullable<Modules['type']>;

export type Blocks = {
    [K in ModuleType]?:  // eslint-disable-next-line @typescript-eslint/no-explicit-any
        | ComponentType<any>
        | (({ children }: { children?: ComponentType; index?: number }) => JSX.Element);
};

const Noop = ({ children }: { children: ComponentType }) => <>{children}</>;

const modules: Blocks = {
    // Important blocks that shouldn't be dynamically loaded
    m130ProductDetailModule: M130ProductDetail,
    m131ProductDetailModule: M131ProductDetail,
    m140ProductListModule: M140ProductsList,

    m0LineModule: dynamic(() => import('../M0Line')),
    m10HeroModule: dynamic(() => import('../hero-blocks/m10hero/m10hero.component')), // moved to css modules
    m11HeroModule: dynamic(() => import('../hero-blocks/m11hero/m11hero.component')), // moved to css modules
    m12HeroModule: dynamic(() => import('../M12Hero')),
    m20CategoriesModule: dynamic(() => import('../m20categories/m20categories.component')), // moved to css modules
    m22CategoriesModule: dynamic(
        () => import('../m22categories-link-list/m22categories-link-list.component')
    ), // moved to css modules
    m23PlpBottomCategoriesModule: dynamic(() => import('../M23PlpBottomCategories')),
    m30ProductCarouselModule: dynamic(() => import('../carousel/m30product-carousel.component'), {
        ssr: false,
    }),
    m31ProductCarouselModule: dynamic(() => import('../carousel/m31product-carousel.component')),
    m32ProductCarouselModule: dynamic(() => import('../carousel/m32product-carousel.component')),
    m40PromotedContentModule: dynamic(() => import('../M40PromotedContent')),
    m60UspModule: dynamic(() => import('../m60-usp/m60-usp.component')), // moved to css modules
    m61UspModule: dynamic(() => import('../m61-usp/m61-usp.component')), // moved to css modules
    m70MediaModule: dynamic(() => import('../M70Media')),
    m75MediaAndTextModule: dynamic(() => import('../M75MediaText')),
    m76MediaAndTextModule: dynamic(() => import('../M76MediaText')),
    m80MediaAndTextBundleModule: dynamic(() => import('../M80MediaTextBundle')),
    m90QuoteModule: dynamic(() => import('../M90Quote')),
    m100RichTextModule: dynamic(() => import('../M100RichText')),
    m110NewsletterSignupModule: dynamic(
        () => import('../m110-newsletter-signup/m110-newsletter-signup.component')
    ), // moved to css modules
    m120AccordionListModule: dynamic(() => import('../M120Accordion')),
    m150BasketModule: dynamic(() => import('../M150Basket'), { ssr: false }),
    m160CheckoutModule: dynamic(() => import('../M160Checkout'), { ssr: false }),
    m170SearchResultsModule: dynamic(() => import('../M170Search')),
    m180CookiePolicyModule: dynamic(() => import('../M180CookiePolicy'), { ssr: false }),
    m190ReceiptModule: dynamic(() => import('../M190Receipt'), { ssr: false }),
    m200ProfileModule: dynamic(() => import('../M200Profile')),
    m210SearchbarModule: dynamic(() => import('../M210Searchbar')),
    m220ReturnsModule: dynamic(() => import('../M220Returns')),
};

type DynamicBlockProps = {
    element: Modules;
    index?: number;
};

export const DynamicBlock = ({ element }: DynamicBlockProps) => {
    const Block = modules[element.type as keyof Blocks] || Noop;
    return (
        <ErrorBoundary fallback={<ErrorFallbackContent />}>
            <Block {...element} />
        </ErrorBoundary>
    );
};
